/**
 * Filtr z pilulek (Linear) — čistá logika bez Reactu, testuje ji
 * `node tests/test_filtr_logika.js`.
 *
 * filtr = { pole, neg, hodnoty } — uvnitř filtru NEBO, mezi filtry A, `neg` obrací.
 * Pole stránky: { klic, nazev, rod: "m"|"z"|"s", hodnotyPolozky(p) → [hodnoty], … }.
 * Pohled (Všechny / Moje / Váznou…) je rozsah stránky — filtr pak zužuje v něm.
 */

export function odpovida(polozka, filtry, pole) {
  return filtry.every((f) => {
    if (!f.hodnoty.length) return true;
    const def = pole.find((p) => p.klic === f.pole);
    if (!def) return true;
    const ma = (def.hodnotyPolozky(polozka) || []).some((v) => f.hodnoty.includes(v));
    return f.neg ? !ma : ma;
  });
}

const OP = {
  m: ["je", "není", "je jeden z", "není žádný z"],
  z: ["je", "není", "je jedna z", "není žádná z"],
  s: ["je", "není", "je jedno z", "není žádné z"],
};
/** Věta operátoru podle rodu pole a počtu hodnot. */
export function operator(rod, neg, pocetHodnot) {
  const t = OP[rod] || OP.m;
  return pocetHodnot > 1 ? t[neg ? 3 : 2] : t[neg ? 1 : 0];
}

/** Do adresy: `faze:NABIDKA,OBJEDNANO;!obchodnik:<id>` (hodnoty přes encodeURIComponent). */
export function zakoduj(filtry) {
  return filtry.filter((f) => f.hodnoty.length)
    .map((f) => `${f.neg ? "!" : ""}${f.pole}:${f.hodnoty.map(encodeURIComponent).join(",")}`).join(";");
}

export function dekoduj(text, pole) {
  if (!text) return [];
  const klice = new Set(pole.map((p) => p.klic));
  return String(text).split(";").map((cast) => {
    const neg = cast.startsWith("!");
    const [klic, hodnoty = ""] = (neg ? cast.slice(1) : cast).split(":");
    const vals = hodnoty.split(",").filter(Boolean).map((v) => { try { return decodeURIComponent(v); } catch { return v; } });
    return { pole: klic, neg, hodnoty: vals };
  }).filter((f) => klice.has(f.pole) && f.hodnoty.length);
}

/** Přidá hodnotu do filtru pole (sloučí se do existující pilulky), nebo ji odebere. */
export function prepniHodnotu(filtry, klic, hodnota) {
  const i = filtry.findIndex((f) => f.pole === klic);
  if (i < 0) return [...filtry, { pole: klic, neg: false, hodnoty: [hodnota] }];
  const f = filtry[i];
  const hodnoty = f.hodnoty.includes(hodnota) ? f.hodnoty.filter((v) => v !== hodnota) : [...f.hodnoty, hodnota];
  return filtry.map((x, j) => (j === i ? { ...f, hodnoty } : x));
}

/** Kolik položek má danou hodnotu pole (pro čísla v menu). */
export function pocty(polozky, def) {
  const out = new Map();
  for (const p of polozky) for (const v of new Set(def.hodnotyPolozky(p) || [])) out.set(v, (out.get(v) || 0) + 1);
  return out;
}

export const bezDiakritiky = (t) => String(t ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Skloňování: sklon(5, ["případ", "případy", "případů"]). */
export function sklon(n, [jedna, dve, pet]) {
  const a = Math.abs(n);
  return a === 1 ? jedna : a >= 2 && a <= 4 ? dve : pet;
}

// ---- Společné hodnoty polí ----

const DEN = 86400000;
const stari = (d, ted = Date.now()) => (d ? Math.floor((ted - new Date(d).getTime()) / DEN) : null);

/** Poslední změna / aktivita: hodnoty se kryjí (7 dní ⊂ 30 dní ⊂ 90 dní), uvnitř filtru je NEBO. */
export const ZMENA_HODNOTY = [
  { v: "D7", label: "Posledních 7 dní" }, { v: "D30", label: "Posledních 30 dní" },
  { v: "D90", label: "Posledních 90 dní" }, { v: "STARSI", label: "Starší než 90 dní" },
];
export function zmenaHodnoty(d, ted = Date.now()) {
  const s = stari(d, ted);
  if (s === null) return [];
  if (s <= 7) return ["D7", "D30", "D90"];
  if (s <= 30) return ["D30", "D90"];
  if (s <= 90) return ["D90"];
  return ["STARSI"];
}

/** Hodnota zakázky v Kč (bez DPH). Nula je platná — patří do nejnižšího pásma. */
export const HODNOTA_HODNOTY = [
  { v: "DO200", label: "do 200 tis. Kč" }, { v: "DO500", label: "200–500 tis. Kč" }, { v: "NAD500", label: "nad 500 tis. Kč" },
];
export function hodnotaPasmo(n) {
  if (n === null || n === undefined) return [];
  return [n < 200000 ? "DO200" : n < 500000 ? "DO500" : "NAD500"];
}

/** Dnešek jako YYYY-MM-DD v místním čase. */
export function dnes(ted = new Date()) {
  const d = new Date(ted);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function plusDni(iso, dni) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + dni);
  return dnes(d);
}
